import { useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import type { PolicyObject } from "@/types/redteam";

interface PolicyConfigurationProps {
  policies: PolicyObject[];
  onChange: (policies: PolicyObject[]) => void;
  required?: boolean;
  description?: string;
  label?: string;
}

// Helper function to generate policy IDs - defined outside component to avoid React compiler issues
function createPolicyId(counter: number): string {
  return `policy-${counter}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Reusable component for configuring multiple policies
 * Each policy has a name (optional) and text (required)
 */
export function PolicyConfiguration({
  policies,
  onChange,
  required = false,
  description,
  label = "Policies",
}: PolicyConfigurationProps) {
  // Use ref to track counter for generating stable IDs
  const idCounterRef = useRef(0);

  const generatePolicyId = () => {
    idCounterRef.current += 1;
    return createPolicyId(idCounterRef.current);
  };

  const handlePolicyChange = (index: number, field: keyof PolicyObject, value: string) => {
    // If policies array is empty, this is a placeholder - create the first real policy
    if (policies.length === 0) {
      onChange([{ id: generatePolicyId(), [field]: value, name: "", text: "" } as PolicyObject]);
      return;
    }

    const updated = [...policies];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleAddPolicy = () => {
    onChange([...policies, { id: generatePolicyId(), name: "", text: "" }]);
  };

  const handleRemovePolicy = (index: number) => {
    const updated = policies.filter((_, i) => i !== index);
    // Ensure at least one policy exists if required
    if (updated.length === 0 && required) {
      onChange([{ id: generatePolicyId(), name: "", text: "" }]);
    } else {
      onChange(updated);
    }
  };

  // If empty, show one placeholder policy card (but don't generate ID during render)
  // Use a stable placeholder ID that won't cause re-renders
  const placeholderPolicy: PolicyObject = useMemo(
    () => ({ id: "policy-placeholder", name: "", text: "" }),
    []
  );

  const displayPolicies = policies.length > 0 ? policies : [placeholderPolicy];
  const canAddMore = !displayPolicies.some((p) => !p.text || p.text.trim() === "");

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <div className="space-y-4">
        {displayPolicies.map((policy, index) => (
          <div key={policy.id || index} className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Policy {index + 1}</Label>
              {displayPolicies.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemovePolicy(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="space-y-2">
              <div>
                <Label htmlFor={`policy-name-${index}`} className="text-xs">
                  Policy Name
                </Label>
                <Input
                  id={`policy-name-${index}`}
                  placeholder="e.g., Data Privacy Policy"
                  value={policy.name || ""}
                  onChange={(e) => handlePolicyChange(index, "name", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor={`policy-text-${index}`} className="text-xs">
                  Policy Text
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Textarea
                  id={`policy-text-${index}`}
                  placeholder="Enter the policy text that describes what should be checked..."
                  value={policy.text || ""}
                  onChange={(e) => handlePolicyChange(index, "text", e.target.value)}
                  className="mt-1 min-h-[120px]"
                />
              </div>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddPolicy}
          disabled={!canAddMore}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Policy
        </Button>
      </div>
    </div>
  );
}
